<template>
  <div class="space-y-1">
    <div :class="{ 'flex items-start justify-between': type === 'checkbox' }">
      <div v-if="type === 'checkbox'" class="flex-grow">
        <label 
          v-if="label" 
          :for="id" 
          :class="[
            'block font-medium text-gray-700 dark:text-white',
            labelSizeClasses[size]
          ]"
        >
          {{ label }}
          <span v-if="required" class="text-red-500">*</span>
        </label>
        <p 
          v-if="hint" 
          :class="[
            'text-gray-500 mt-1 dark:text-gray-400',
            hintSizeClasses[size]
          ]"
        >
          {{ hint }}
        </p>
      </div>

      <!-- Checkbox input -->
      <div v-if="type === 'checkbox'" class="flex items-center h-5">
        <input
          :id="id"
          type="checkbox"
          :checked="modelValue"
          @change="updateCheckboxValue"
          v-bind="$attrs"
          :class="[
            'form-checkbox h-6 w-6 rounded',
            'transition-colors duration-200 ease-in-out',
            error ? 'border-red-300 text-red-500 focus:ring-red-500' : [
              'border-gray-300 dark:border-gray-600',
              'text-primary focus:ring-primary',
              'dark:text-primary dark:focus:ring-primary',
              'focus:ring-offset-0 dark:focus:ring-offset-gray-900'
            ],
            'cursor-pointer'
          ]"
        />
      </div>

      <!-- Non-checkbox fields container -->
      <div v-else class="w-full">
        <label 
          v-if="label" 
          :for="id" 
          :class="[
            'block font-medium text-gray-700 dark:text-gray-400 px-2 py-1',
            labelSizeClasses[size]
          ]"
        >
          {{ label }}
          <span v-if="required" class="text-red-500">*</span>
        </label>

        <div :class="{ 'relative': type === 'password' }">
          <!-- Text, Email, Password inputs -->
          <input
            v-if="['text', 'email', 'password', 'date'].includes(type ?? '')"
            :id="id"
            :type="showPassword ? 'text' : type"
            :value="modelValue"
            @input="updateValue"
            v-bind="$attrs"
            :class="[
              'block w-full shadow-sm border font-semibold dark:bg-gray-900 dark:text-white',
              formRoundingClasses.input,
              inputSizeClasses[size],
              error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' :
                     'border-gray-300 dark:border-gray-700 focus:border-primary focus:ring-primary'
            ]"
          />

          <!-- Password toggle button -->
          <button
            v-if="type === 'password'"
            type="button"
            :class="[
              'absolute right-0 flex items-center pr-3',
              buttonPositionClasses[size]
            ]"
            @click="showPassword = !showPassword"
          >
            <UIIcon
              :name="showPassword ? 'eye-off' : 'eye'"
              class="h-5 w-5 text-gray-400"
            />
          </button>

          <!-- Select input -->
          <select
            v-else-if="type === 'select'"
            :id="id"
            :value="modelValue"
            @input="updateValue"
            v-bind="$attrs"
            :class="[
              'block w-full border shadow-sm dark:bg-gray-900 dark:text-white',
              'appearance-none bg-[center_right_0.5rem] bg-no-repeat',
              'select-chevron',
              formRoundingClasses.input,
              inputSizeClasses[size],
              error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' :
                     'border-gray-300 dark:border-gray-700 focus:border-primary focus:ring-primary'
            ]"
          >
            <option v-if="placeholder" value="" :disabled="required">
              {{ placeholder }}
            </option>
            <option
              v-for="option in options"
              :key="option.value"
              :value="option.value"
              class="py-2 dark:bg-gray-900 dark:text-white"
            >
              {{ option.label }}
            </option>
          </select>

          <!-- Textarea input -->
          <textarea
            v-else-if="type === 'textarea'"
            :id="id"
            :value="modelValue"
            @input="updateValue"
            v-bind="$attrs"
            :class="[
              'block w-full border shadow-sm dark:bg-gray-900 dark:text-white',
              formRoundingClasses.input,
              inputSizeClasses[size],
              error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' :
                     'border-gray-300 dark:border-gray-700 focus:border-primary focus:ring-primary'
            ]"
          />
        </div>
      </div>

      <!-- Add helper text -->
      <div 
        v-if="helperText"
        class="mt-2 whitespace-pre-line text-gray-500 dark:text-gray-400"
        :class="[hintSizeClasses[size]]"
      >
        {{ helperText }}
      </div>
    </div>

    <p 
      v-if="error" 
      :class="[
        'text-red-600',
        hintSizeClasses[size]
      ]"
    >
      {{ error }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { getConfig, getFormRoundingClasses } from '@/config'
import UIIcon from '@/components/ui/UIIcon.vue'
import { sizeClasses, labelSizeClasses, buttonPositionClasses, hintSizeClasses } from '@/config/sizes'
import { IFormField, AppTheme } from '@/types'

const props = withDefaults(defineProps<{
  modelValue: any
  field: IFormField
  appId?: AppTheme
  label?: string
  type?: string
  required?: boolean
  error?: string
  hint?: string
  helperText?: string
  placeholder?: string
  options?: Array<{ label: string, value: any }>
  size?: 'sm' | 'md' | 'lg'
}>(), {
  size: 'md'
})

const emit = defineEmits(['update:modelValue'])

const config = getConfig()
const formRoundingClasses = getFormRoundingClasses(config)
const id = computed(() => `field-${Math.random().toString(36).slice(2)}`)
const showPassword = ref(false)
const inputSizeClasses = sizeClasses

const updateValue = (event: Event) => {
  const target = event.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  emit('update:modelValue', target.value)
}

const updateCheckboxValue = (event: Event) => {
  const target = event.target as HTMLInputElement
  emit('update:modelValue', target.checked)
}
</script>

<style>
.select-chevron {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3E%3C/svg%3E");
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
}

.dark .select-chevron {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%239ca3af' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3E%3C/svg%3E");
}

/* Custom checkbox styles */
.form-checkbox {
  @apply appearance-none border-2;
  background-origin: border-box;
}

.form-checkbox:checked {
  background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3e%3c/svg%3e");
  background-color: currentColor;
  background-position: center;
  background-repeat: no-repeat;
  background-size: 100% 100%;
}

.form-checkbox:disabled {
  @apply opacity-50 cursor-not-allowed;
}

.dark .form-checkbox:checked {
  border-color: transparent;
}
</style> 